/**
 * v0.9.0 §1.1–§1.2 — planar presence.
 *
 * A module (or process) exists on a set of PLANES. Sources, in order:
 *   - born-on: an `on <planes>` declaration prefix (replaces primary);
 *   - also-on: a body `on p1, p2` (additive to the birth set);
 *   - inclusion: a bare reference inside a top-level `on <plane> { … }` block;
 *   - nesting: children inherit their parent's planes;
 *   - membership: a ref-valued aspect (`aspect deployment: Euro as active`)
 *     puts the MEMBER on the KEY's plane, and PULLS the referenced PLACE onto
 *     that plane too (the key's plane only — the place's other planes never
 *     transfer). Rosters (`aspect <key> for: A as x`) are the place-side
 *     spelling of the same fact.
 *
 * The default with no source is `primary` — the implicit structural plane.
 */

import type { QualifiedName, OnInclusion, Value } from "./types.js";
import type { ResolvedModel, ResolvedModule } from "./resolver.js";
import { resolveFlatModuleRef } from "./hoist-index.js";

export const PRIMARY_PLANE = "primary";

export interface PresenceInfo {
  /** Qualified module path (dot-joined) → the set of plane names it is on. */
  readonly planesOf: ReadonlyMap<string, ReadonlySet<string>>;
  /** The same sets counting DECLARED sources only — born-on, also-on, inclusion
   *  and nesting, but never membership. A membership aspect trivially puts its
   *  member on the key's plane, so "was it already there?" can only be asked of
   *  this map (§1.3 same-plane aspect). */
  readonly declaredPlanesOf: ReadonlyMap<string, ReadonlySet<string>>;
  /** Every non-primary plane name seen in the model, sorted. */
  readonly planes: readonly string[];
  /** Place path → instance name → member module path (from `as` naming).
   *  Instance names are node-local — they never cascade (§1.3). */
  readonly instancesAt: ReadonlyMap<string, ReadonlyMap<string, string>>;
  /** §7: qualified paths of everything a partial-presence block declared that
   *  therefore does NOT exist on primary — interfaces, surfaces and fields, and
   *  the submodules/processes the block declared (those reach presence as
   *  born-on nodes, but they are members a plane hides all the same). A
   *  top-level `on p module X` is NOT here: it is a plane-scoped module in its
   *  own right, which the default board has always drawn. Empty in a model that
   *  uses no partial presence — which is exactly what lets a renderer skip the
   *  primary-plane narrowing, and keep projecting by reference. */
  readonly planeExclusiveMembers: ReadonlySet<string>;
}

const joinPlanes = (planes: readonly QualifiedName[] | undefined): string[] =>
  (planes ?? []).map((p) => p.join("."));

/** Atomic ref-valued items of a label value (identifier / ref / list items). */
function refItems(v: Value | undefined): { path: readonly string[]; instance?: string }[] {
  if (!v) return [];
  if (v.kind === "identifier") return [{ path: [v.text] }];
  if (v.kind === "ref") return [{ path: v.path, instance: v.instance }];
  if (v.kind === "list") return v.items.flatMap(refItems);
  return [];
}

/**
 * v0.9.0 §1.2: resolve a partial-presence reference against the members of the
 * module that owns the block. References are MEMBER paths (§1.2) — a submodule,
 * interface, surface or field of the main body, possibly dotted — never a
 * hoisted name from elsewhere: a presence block projects THIS node's members.
 *
 * Returns the member path relative to the owner (so the caller can key it) and
 * whether it is a module. Shared by `computePresence` (which rides the plane
 * onto the member) and validation (which errors when this returns undefined) so
 * the two can never disagree about what resolves.
 */
export function resolvePresenceRef(
  owner: ResolvedModule,
  segs: readonly string[]
): { path: string; isModule: boolean } | undefined {
  if (segs.length === 0) return undefined;
  const path = segs.join(".");
  const hasField = (m: ResolvedModule, name: string): boolean =>
    m.body.fields.some((f) => f.path.join(".") === name);
  // A FIELD path is itself dotted (`retry.limit`, `widget.color`), and so is a
  // path that walks submodules (`Inner.health`) — the two are indistinguishable
  // by shape, so the order below is the tie-break: a field the author WROTE,
  // then the submodule walk, then any field at all. The walk alone would treat
  // `retry` as a child module, miss, and give up before the field was
  // considered; probing every field first would answer `widget.icon` with the
  // template `widget.*` field every module type stamps, shadowing a submodule
  // named `widget` the author did write.
  const authoredField = (m: ResolvedModule, name: string): boolean =>
    m.decl.body.fields.some((f) => f.path.join(".") === name);
  if (authoredField(owner, path)) return { path, isModule: false };
  let cur = owner;
  for (let i = 0; i < segs.length - 1; i++) {
    const next = cur.modules.find((c) => c.name === segs[i]);
    // Not a submodule chain after all — what remains may still be a dotted
    // field of the node we walked to (`Inner.retry.limit`).
    if (!next) return hasField(cur, segs.slice(i).join(".")) ? { path, isModule: false } : undefined;
    cur = next;
  }
  const last = segs[segs.length - 1]!;
  if (cur.modules.some((c) => c.name === last)) return { path, isModule: true };
  if (cur.interfaces.some((i) => i.name === last)) return { path, isModule: false };
  if (cur.surfaces.some((f) => f.name === last)) return { path, isModule: false };
  if (hasField(cur, last)) return { path, isModule: false };
  return undefined;
}

/**
 * Compute the presence sets for every module. `onInclusions` come from the
 * parsed file (top-level `on` blocks referencing modules declared elsewhere).
 */
export function computePresence(
  model: ResolvedModel,
  onInclusions?: readonly OnInclusion[]
): PresenceInfo {
  const explicit = new Map<string, Set<string>>(); // pre-inheritance sets
  const byPath = new Map<string, ResolvedModule>();
  const byLastSeg = new Map<string, string[]>();

  const index = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const key = path.join(".");
    byPath.set(key, m);
    const last = m.name;
    const cands = byLastSeg.get(last) ?? [];
    cands.push(key);
    byLastSeg.set(last, cands);
    const set = new Set<string>();
    // born-on replaces primary; plain declaration = primary.
    const born = joinPlanes(m.decl.onPlanes);
    if (born.length) for (const p of born) set.add(p);
    else set.add(PRIMARY_PLANE);
    // also-on is additive.
    for (const p of joinPlanes(m.decl.body.alsoOn)) set.add(p); // raw decl — body merge drops extras
    explicit.set(key, set);
    for (const c of m.modules) index(c, path);
  };
  for (const m of model.modules) index(m, []);

  // §1.2 partial presence, the member-level half. A submodule/process declared
  // in a block was stamped born-on at fold time, so it is already exclusive
  // above; interfaces, surfaces and fields have no plane slot, so their sets are
  // built here and stamped after inheritance (they are not modules to walk).
  const memberPlanes = new Map<string, Set<string>>();
  // Per member, the planes a block DECLARED it on vs the planes a block only
  // REFERENCED it on — combined after every block is read, so file order can't
  // change the answer.
  const memberDeclared = new Map<string, Set<string>>();
  const memberRefs = new Map<string, Set<string>>();
  // The planes a module's own partial blocks name — the node RIDES to each of
  // them with a reduced surface (§7 "only the listed members ride to p1"), so
  // the plane is added to the carrier below.
  const carrierPlanes = new Map<string, Set<string>>();
  // Submodules and processes declared inside a block are stamped born-on at
  // FOLD time, so they reach presence as ordinary plane-scoped nodes and never
  // pass through `memberPlanes`. Their keys are held here so the exclusivity
  // set below can still count them: they are plane-exclusive MEMBERS, and the
  // board's "does this model need narrowing?" gate reads that set. A top-level
  // `on p module X` is NOT one of these — it is a plane-scoped module in its
  // own right, and the default board has always drawn it.
  const blockDeclaredNodes = new Set<string>();
  const forPartials = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const key = path.join(".");
    for (const pp of m.decl.body.partialOn ?? []) {
      const plane = pp.plane.join(".");
      const carrier = carrierPlanes.get(key) ?? new Set<string>();
      carrier.add(plane);
      carrierPlanes.set(key, carrier);
      // Declarations are plane-EXCLUSIVE; references ride AND keep primary.
      // Both are recorded per member and only COMBINED once every block has
      // been read (below) — deciding the seed here would make the answer depend
      // on which block came first in the file, and presence is a set of
      // declarative facts, not a sequence.
      const declare = (memberKey: string): void => {
        (memberDeclared.get(memberKey) ?? memberDeclared.set(memberKey, new Set()).get(memberKey)!).add(plane);
      };
      for (const i of pp.body.interfaces) declare(`${key}.${i.name}`);
      for (const f of pp.body.surfaces) declare(`${key}.${f.name}`);
      for (const f of pp.body.fields) declare(`${key}.${f.path.join(".")}`);
      // A block declares SUBMODULES under `modules` — but so does the
      // type-named spelling of a surface (`api Health { … }`), which the
      // resolver reclassifies afterwards. Only a name that really became a
      // submodule has a node in the walk below; everything else is a member,
      // and `surface Health` must not mean something different from
      // `api Health`. Processes are never nodes, so they are members too.
      for (const c of pp.body.modules) {
        if (m.modules.some((x) => x.name === c.name)) blockDeclaredNodes.add(`${key}.${c.name}`);
        else declare(`${key}.${c.name}`);
      }
      for (const p of pp.body.processes) declare(`${key}.${p.name}`);
      // A ref that reaches THROUGH a submodule (`Inner.health`) needs that
      // submodule on the plane as well — an interface whose own node is absent
      // has nothing to hang off. Each one rides as a carrier, so its untouched
      // members stay behind exactly as the owner's do.
      const rideIntermediates = (memberPath: string): void => {
        const segs = memberPath.split(".");
        let cur = m;
        let acc = key;
        for (let i = 0; i < segs.length - 1; i++) {
          const next = cur.modules.find((c) => c.name === segs[i]);
          if (!next) return; // a dotted FIELD path, not a chain of nodes
          acc = `${acc}.${segs[i]}`;
          (carrierPlanes.get(acc) ?? carrierPlanes.set(acc, new Set()).get(acc)!).add(plane);
          cur = next;
        }
      };
      for (const ref of pp.refs ?? []) {
        const hit = resolvePresenceRef(m, ref.path);
        if (!hit) continue; // unresolved — validation owns the error
        const memberKey = `${key}.${hit.path}`;
        rideIntermediates(hit.path);
        if (hit.isModule) {
          explicit.get(memberKey)?.add(plane);
          continue;
        }
        (memberRefs.get(memberKey) ?? memberRefs.set(memberKey, new Set()).get(memberKey)!).add(plane);
      }
    }
    for (const c of m.modules) forPartials(c, path);
  };
  for (const m of model.modules) forPartials(m, []);

  // Combine the two halves. A member a block DECLARED exists on those planes
  // ONLY — primary is deliberately absent, and a reference elsewhere doesn't
  // put it back (the declaration is what says "this doesn't exist on primary").
  // A member only ever REFERENCED is a main-body member riding along, so it
  // keeps primary.
  for (const [memberKey, declared] of memberDeclared) {
    const set = new Set(declared);
    for (const p of memberRefs.get(memberKey) ?? []) set.add(p);
    memberPlanes.set(memberKey, set);
  }
  for (const [memberKey, refs] of memberRefs) {
    if (memberDeclared.has(memberKey)) continue;
    memberPlanes.set(memberKey, new Set([PRIMARY_PLANE, ...refs]));
  }

  // A written reference resolves to a module path (hoisting-lite; ambiguity →
  // no match, the aspect-ref validator owns the diagnostic). Shared with the
  // stub pass so an inclusion this pass resolves is never a ghost to that one.
  const resolveRef = (path: readonly string[]): string | undefined =>
    resolveFlatModuleRef(path, byPath, byLastSeg);

  // Inclusions add planes to the referenced module. An UNRESOLVED target is a
  // ghost `synthesizeStubs` materializes at exactly the written path (§1.2), so
  // its planes are held aside and stamped onto that path below — otherwise the
  // stub would draw on primary only, on a plane it was explicitly included on.
  const ghostPlanes = new Map<string, Set<string>>();
  for (const inc of onInclusions ?? []) {
    const target = resolveRef(inc.target);
    const planes = joinPlanes(inc.planes);
    if (!target) {
      const key = inc.target.join(".");
      const set = ghostPlanes.get(key) ?? new Set<string>([PRIMARY_PLANE]);
      for (const p of planes) set.add(p);
      ghostPlanes.set(key, set);
      continue;
    }
    const set = explicit.get(target);
    if (set) for (const p of planes) set.add(p);
  }

  // Snapshot the declared-only sets before membership folds in (§1.3).
  const declared = new Map<string, Set<string>>();
  for (const [k, v] of explicit) declared.set(k, new Set(v));

  const instancesAt = new Map<string, Map<string, string>>();
  const addInstance = (place: string, instance: string | undefined, member: string): void => {
    if (!instance) return;
    const m = instancesAt.get(place) ?? new Map<string, string>();
    m.set(instance, member);
    instancesAt.set(place, m);
  };

  // Membership: member-side ref aspects + place-side rosters. Both put the
  // member AND the place on the key's plane (membership presence + pull).
  const membership = (
    ownerKey: string,
    aspects: readonly { path: QualifiedName; value?: Value }[],
    rosters: readonly { key: QualifiedName; entries: readonly { path: QualifiedName; instance?: string }[] }[] | undefined
  ): void => {
    for (const l of aspects) {
      const plane = l.path.join(".");
      for (const item of refItems(l.value)) {
        const place = resolveRef(item.path);
        explicit.get(ownerKey)?.add(plane);
        if (place) {
          explicit.get(place)?.add(plane);
          addInstance(place, item.instance, ownerKey);
        }
      }
    }
    for (const r of rosters ?? []) {
      const plane = r.key.join(".");
      explicit.get(ownerKey)?.add(plane); // placehood pull: declaring a roster makes you a place
      for (const e of r.entries) {
        const member = resolveRef(e.path);
        if (member) {
          explicit.get(member)?.add(plane);
          addInstance(ownerKey, e.instance, member);
        }
      }
    }
  };
  const walkMembership = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    membership(path.join("."), m.body.aspects, m.decl.body.rosters);
    for (const c of m.modules) walkMembership(c, path);
  };
  for (const m of model.modules) walkMembership(m, []);

  // Nesting inheritance: children union their parent's planes.
  const inheritAll = (source: ReadonlyMap<string, ReadonlySet<string>>): Map<string, Set<string>> => {
    const out = new Map<string, Set<string>>();
    const inherit = (m: ResolvedModule, parent: readonly string[], parentSet: ReadonlySet<string> | undefined): void => {
      const path = [...parent, m.name];
      const key = path.join(".");
      const set = new Set(source.get(key) ?? [PRIMARY_PLANE]);
      // A child born plain (primary-only) inside a plane-scoped parent lives on
      // the parent's planes (presence inheritance); explicit born-on stays.
      if (parentSet && !(m.decl.onPlanes && m.decl.onPlanes.length)) {
        for (const p of parentSet) set.add(p);
        if (!parentSet.has(PRIMARY_PLANE)) set.delete(PRIMARY_PLANE);
      }
      out.set(key, set);
      for (const c of m.modules) inherit(c, path, set);
    };
    for (const m of model.modules) inherit(m, [], undefined);
    return out;
  };
  const planesOf = inheritAll(explicit);
  const declaredPlanesOf = inheritAll(declared);
  // A partial block puts the CARRIER on the plane too — the node rides with a
  // reduced surface. Stamped AFTER inheritance so children keep the parent's
  // declared planes (a submodule doesn't ride just because a sibling member
  // does), and the carrier's main-body members are pinned to the pre-partial
  // set first — projection falls back to the owner's planes for an untagged
  // member, which would otherwise drag the whole main body onto the plane.
  const stampCarriers = (m: ResolvedModule, parent: readonly string[]): void => {
    const path = [...parent, m.name];
    const key = path.join(".");
    const partial = carrierPlanes.get(key);
    if (partial) {
      const base = planesOf.get(key) ?? new Set<string>([PRIMARY_PLANE]);
      const baseDeclared = declaredPlanesOf.get(key) ?? new Set<string>([PRIMARY_PLANE]);
      // Members and modules share one key namespace (`A.B` reads the same for a
      // submodule and an interface — `buildNodeUniverse` has the same rule). A
      // submodule already owns its entry from the walk, and its planes are its
      // own, so pinning must never write over one.
      const childModuleKeys = new Set(m.modules.map((c) => `${key}.${c.name}`));
      const pin = (memberKey: string): void => {
        if (memberPlanes.has(memberKey)) return; // rode in on a block
        if (childModuleKeys.has(memberKey)) return; // a submodule owns this key
        planesOf.set(memberKey, new Set(base));
        declaredPlanesOf.set(memberKey, new Set(baseDeclared));
      };
      for (const i of m.interfaces) pin(`${key}.${i.name}`);
      for (const s of m.surfaces) pin(`${key}.${s.name}`);
      for (const f of m.body.fields) pin(`${key}.${f.path.join(".")}`);
      for (const p of partial) {
        base.add(p);
        baseDeclared.add(p);
      }
      planesOf.set(key, base);
      declaredPlanesOf.set(key, baseDeclared);
    }
    for (const c of m.modules) stampCarriers(c, path);
  };
  for (const m of model.modules) stampCarriers(m, []);

  // Non-module members have no node in the walk, so they are stamped after it.
  // Partial presence is a DECLARED source, so both maps get them.
  const planeExclusiveMembers = new Set<string>();
  for (const [key, set] of memberPlanes) {
    planesOf.set(key, set);
    declaredPlanesOf.set(key, new Set(set));
    if (!set.has(PRIMARY_PLANE)) planeExclusiveMembers.add(key);
  }
  // A block-declared submodule/process rode in as a born-on node, so its planes
  // come off the module walk — but it is still a member that a plane hides, and
  // the board's narrowing gate reads this set.
  for (const key of blockDeclaredNodes) {
    const set = planesOf.get(key);
    if (set && !set.has(PRIMARY_PLANE)) planeExclusiveMembers.add(key);
  }
  // The ghosts have no module to walk, so they are stamped after inheritance.
  for (const [key, set] of ghostPlanes) {
    if (planesOf.has(key)) continue; // a module by that name exists after all
    planesOf.set(key, set);
    declaredPlanesOf.set(key, new Set(set));
  }

  const all = new Set<string>();
  for (const s of planesOf.values()) for (const p of s) if (p !== PRIMARY_PLANE) all.add(p);

  return { planesOf, declaredPlanesOf, planes: [...all].sort(), instancesAt, planeExclusiveMembers };
}
